import {useState, useEffect, useContext, useRef } from 'react'
import { Box, Container, Typography } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom';

import TitleText from '../components/TitleText'
import { getProductCustomize } from '../services/productService';
import { addItemToCart, recalculateCustomProduct, checkPrimarySelected, calculateCustomProductTotals, buildPriceMatrixMap, getSelectedMap, cleanupCartItems, cleanupProduct } from '../utils/utils';
import InputSelect from '../components/productVariants/InputSelect';
import ProductResumeCard from '../components/productVariants/ProductResumeCard';
import CustomBreadcrumbs from '../components/CustomBreadcrumbs';
import Footer from '../components/Footer';
import { UserDispatchContext } from '../context/UserContext';
import { userCartAddItem } from '../services/userCartService';
import { useSnackbar } from 'notistack';
import { exchangeRatesByCode } from '../services/ExchangeRateService';
import CustomModal from '../components/CustomModal';
import ButtonStyled from '../components/ButtonStyled';


const styles = {
    stackGrid: {
        display: 'flex',
        flexDirection: 'column',
        gap:{ xs:'2rem', lg:'1.88rem' }
    },
    title: {
        display:{ xs: 'none', md:'block'},
        color: '#303030',
        fontSize: { md:'1.5625rem' },
        fontWeight: 700,
    },
    buttonAddToCart: {
        color: '#D2491A',
        backgroundColor: '#FEDED2',
        '&:hover': {
            backgroundColor: '#FEDED2'
        }
    },
    buttonAddToCartFinalize: {
        color: 'white',
        backgroundColor: '#FC581F'
    }
}

const CustomizeProductPage = () => {
    const { enqueueSnackbar } = useSnackbar();
    const globalUserDispatchContext = useContext(UserDispatchContext);
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [pathTree, setPathTree] = useState([]);
    const [product, setProduct] = useState([]);
    const [variantsTree, setVariantsTree] = useState([]);

    const [allCharacteristics, setAllCharacteristics] = useState([]);
    const [characteristicListData, setCharacteristicListData] = useState([]);

    const [selectedVariant, setSelectedVariant] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [dollarPrice, setDollarPrice] = useState(0);
    const [priceMatrixMap, setPriceMatrixMap] = useState(new Map());

    const [missingPrimarySelected, setMissingPrimarySelected] = useState(true);

    const hasInitializedDefaults = useRef(false);
    const hasInitializedPrimary = useRef(false);
    const hasSanitized = useRef(false);

    const [customVariant, setCustomVariant] = useState({
        _id: "",
        characteristics: [],
        createdAt: "",
        extraDeliveryTime: 0,
        height: 0,
        longitude: 0,
        weight: 0,
        stock: 0,
        sku: ""
    });

    const customVariantRef = useRef(customVariant);

    const [openModal, setOpenModal] = useState(false);
    
    useEffect(() => {
        getData();
        getExchangeRateByCode();
    }, [])

    // Get all characteristics from the product
    useEffect(() => {
        if (product.characteristics) {
            setAllCharacteristics(product.characteristics);
            setCharacteristicListData(product.characteristics);
        }
    }, [product])


    // Sanitize custom variant to remove invalid values based on dependency rules
    const sanitizeCustomVariant = (variant, characteristicsData) => {
        let hasChanges = false;
        
        const updatedCharacteristics = variant.characteristics.map(c => {
            const charData = characteristicsData.find(cd => cd.characteristicId === c.characteristicId);
            if (!charData) return c;

            const allowedValues = charData.values?.map(v => v.valueName) || [];

            // If the selected value is no longer allowed, reset it completely
            if (c.valueName && !allowedValues.includes(c.valueName)) {
                // console.log(`⚠️ Sanitizing: ${c.characteristicName} - "${c.valueName}" is no longer allowed`);
                hasChanges = true;
                return { 
                    ...c, 
                    valueName: "", 
                    code: "",
                    // Clear all modifiers to prevent stale data
                    extraPrice: 0,
                    weightModifier: 0,
                    heightModifier: 0,
                    widthModifier: 0,
                    longitudeModifier: 0,
                    stock: 0,
                    extraDeliveryTime: 0
                };
            }

            return c;
        });

        if (hasChanges) {
            console.log("⚠️ Some values were cleared due to dependency rules");
        }

        return { ...variant, characteristics: updatedCharacteristics };
    };

    // Initialize product with default values after loading data
    useEffect(() => {
        if (!product || !customVariant?.characteristics?.length || !priceMatrixMap?.size || !allCharacteristics.length) return;
        if (hasInitializedDefaults.current) return;
      
        // Stage 1 — primary
        const primary = product.characteristics.find(c => c.primary);
        if (primary?.values?.length) {
          handleOnchange(primary.characteristicName, primary.values[0].valueName, 0);
        }
      
        // Stage 2 — other characteristics
        product.characteristics
          .filter(c => !c.primary)
          .forEach((char) => {
            if (char.values?.length) {
              handleOnchange(char.characteristicName, char.values[0].valueName, 0);
            }
          });
      
        hasInitializedDefaults.current = true;
    }, [product, customVariant, priceMatrixMap, allCharacteristics]);

    // Sanitize after initialization and characteristicListData has been updated with dependency rules
    useEffect(() => {
        // Only run after initialization is complete
        if (!hasInitializedDefaults.current) return;
        // Only run once
        if (hasSanitized.current) return;
        // Wait for characteristicListData to be populated
        if (!characteristicListData.length) return;
        
        let sanitized = sanitizeCustomVariant(customVariantRef.current, characteristicListData);
        
        // Check if any values were cleared
        const hasChanges = sanitized.characteristics.some((char, idx) => 
            char.valueName !== customVariantRef.current.characteristics[idx].valueName
        );

        if (hasChanges) {
            console.log("✅ Sanitization complete - invalid values removed");
            
            // Recalculate totals after sanitization
            const calculatedTotals = calculateCustomProductTotals(sanitized, product);
            sanitized = {
                ...sanitized,
                ...calculatedTotals
            };
            
            // Rebuild SKU after sanitization
            sanitized.sku = buildSKU(sanitized);
            
            setCustomVariant(sanitized);
            customVariantRef.current = sanitized;
        }

        hasSanitized.current = true;
    }, [characteristicListData]); // Trigger when characteristicListData is updated      

    
    const getData = async () => {
        const routeSpliced = pathname.split('/');
        const productId = routeSpliced[routeSpliced.length-1];
        const result = await getProductCustomize(productId);
        if(!result.product || !Object.keys(result.product).length){
            navigate(`${pathname}/not-found`);
            return;
        }
        // console.log("RESULT PRODUCT", result.product);
        setProduct(result.product);

        setVariantsTree(result.variantsTree);
        // updateTreeList(result.variantsTree, [], result.product.productVariants);

        // console.log("RESULT PRODUCT VARIANT MATRIX", result.product.variantMatrix);
        const priceMap = buildPriceMatrixMap(result.product.variantMatrix, result.product.characteristics);
        setPriceMatrixMap(priceMap);

        // console.log("PRICE MATRIX MAP", priceMap);

        setCharacteristicListData(result.product.characteristics);  

        const customVariantObj = {
            _id: result.product._id,
            characteristics: result.product.characteristics.map(char => {
                return {
                    characteristicId: char.characteristicId,
                    characteristicName: char.characteristicName,
                    primary: char.primary || false,
                    code: "",
                    valueName: "",
                    weightModifier: 0,
                    heightModifier: 0,
                    widthModifier: 0,
                    longitudeModifier: 0,
                    stock: 0,
                    extraDeliveryTime: 0,
                    extraPrice: 0,
                }
            }),

            createdAt: result.product.createdAt,
            extraDeliveryTime: parseInt(result.product.extraDeliveryTime),
            height: parseFloat(result.product.height),
            width: parseFloat(result.product.width),
            longitude: parseFloat(result.product.longitude),
            weight: parseFloat(result.product.weight),
            price: 0,
            basePrice: result.product.price,
            stock: parseInt(result.product.stock),
            sku: result.product.code,
            code: result.product.code
        }

        // console.log("CUSTOM VARIANT OBJECT", customVariantObj);

        setCustomVariant(customVariantObj);
        customVariantRef.current = customVariantObj;
        // console.log("CUSTOM VARIANT REF", customVariantRef.current);
    };


    const getExchangeRateByCode = async () => {
        setIsLoading(true);
        let { message, result, exchangeRate } = await exchangeRatesByCode("USD");
        if (result) {
          setDollarPrice(exchangeRate.exchangeRate);
          setIsLoading(false);
        } else {
            setDollarPrice(0);
            enqueueSnackbar(message, {
                variant: "error",
            });
        }
    };


    // Set the selected characteristic value
    const setSelectedCharacteristicValue = (charName, indexOption, currCustomVariant) => {
        // console.log("CURRENT CUSTOM VARIANT BEFORE SETTING SELECTED CHARACTERISTIC VALUE", currCustomVariant);

        let targetChar = characteristicListData.find(c => c.characteristicName === charName);
        if (!targetChar) return;

        const targetValue = targetChar.values[indexOption];

        targetChar.valueName = targetValue?.valueName || "";
        targetChar.code = targetValue?.code || "";

        currCustomVariant.characteristics = currCustomVariant.characteristics.map(c => {
            if (c.characteristicName === charName) {
                return { ...c, valueName: targetValue?.valueName || "", code: targetValue?.code || "" };
            }
            return c;
        });

        return currCustomVariant;
    }
    
    // Build the SKU for the custom variant
    const buildSKU = (updatedCustomVariant) => {
        const primChar = updatedCustomVariant.characteristics.find(c => c.primary);
        const primaryCode = primChar?.code || "";
        
        // Include empty codes to show gaps (--) when characteristics are missing values
        const generalCharCodes = updatedCustomVariant.characteristics
            .filter(c => !c.primary)
            .map(c => c.code || "")
            .join('-');

        return updatedCustomVariant.code + primaryCode + (generalCharCodes ? '-' + generalCharCodes : "");
    }
    
    // Handle the change in the characteristic
    const handleOnchange = (charName, newValue, indexOption) => {
        // console.log("UPDATING CHARACTERISTIC", charName, newValue, indexOption);

        let currUpdatedCustomProduct = customVariantRef.current;
        let baseUpdatedCharacteristicListData = allCharacteristics;

        const isPrimary = characteristicListData.find(c => c.characteristicName === charName)?.primary ?? false;
    
        // Primary guard
        if (!isPrimary) {
            const primarySelected = checkPrimarySelected(currUpdatedCustomProduct);
            if (!primarySelected) {
                // console.log("NO PRIMARY SELECTED");

                const primaryCharacteristicName = characteristicListData.find(c => c.primary)?.characteristicName;
                enqueueSnackbar(`Selecciona un valor para ${primaryCharacteristicName} para comenzar.`, {
                    variant: "error"
                });
                return;
            }
        } else {
            setMissingPrimarySelected(false);
        }

        // console.log("UPDATED CUSTOM VARIANT BEFORE", updatedCustomProduct);

        // Reset all characteristic modifiers
        currUpdatedCustomProduct.characteristics = currUpdatedCustomProduct.characteristics.map(c => ({
            ...c,
            extraPrice: 0,
            weightModifier: 0,
            heightModifier: 0,
            widthModifier: 0,
            longitudeModifier: 0,
            stock: 0,
            extraDeliveryTime: 0
        }));      

        // console.log("SETTING SELECTED CHARACTERISTIC VALUE", charName, indexOption);
        currUpdatedCustomProduct = setSelectedCharacteristicValue(charName, indexOption, currUpdatedCustomProduct);
        // console.log("UPDATED CUSTOM VARIANT AFTER SELECTED CHARACTERISTIC VALUE", currUpdatedCustomProduct);

        let { updatedCustomProduct, updatedCharacteristicListData } = recalculateCustomProduct({
            updatedCustomProduct: currUpdatedCustomProduct,
            updatedCharacteristicListData: baseUpdatedCharacteristicListData,
            baseProduct: product,
            priceMatrixMap: priceMatrixMap,
            selectedMap: getSelectedMap(currUpdatedCustomProduct)
        });

        // Sanitize the updated product to remove any values that are no longer valid
        // after dependency rules have been applied
        updatedCustomProduct = sanitizeCustomVariant(updatedCustomProduct, updatedCharacteristicListData);

        // Reset totals to product values
        updatedCustomProduct = {
            ...updatedCustomProduct,
            price: Number(product.price),
            weight: Number(product.weight),
            height: Number(product.height),
            width: Number(product.width),
            longitude: Number(product.longitude)
        };

        // console.log("CALCULATING TOTALS");
        const calculatedTotals = calculateCustomProductTotals(updatedCustomProduct, product);
        updatedCustomProduct = {
            ...updatedCustomProduct,
            ...calculatedTotals
        }
        // console.log("CALCULATED TOTALS", calculatedTotals);

        // console.log("BUILDING SKU");
        updatedCustomProduct.sku = buildSKU(updatedCustomProduct);
        // console.log("SKU", updatedCustomProduct.sku);

        // console.log("SETTING CHARACTERISTIC LIST DATA", updatedCharacteristicListData);
        setCharacteristicListData(updatedCharacteristicListData);

        // console.log("SETTING CUSTOM VARIANT");
        setCustomVariant({
            ...updatedCustomProduct,
        })
        customVariantRef.current = {
            ...updatedCustomProduct,
        };
        // console.log("CUSTOM VARIANT", updatedCustomProduct);
    };

    const buildVariantObject = () => {
        return {
            ...product,
            productVariants: [{
                _id: product._id,
                characteristics: customVariant.characteristics,
                sku: customVariant.sku,
                longitude: customVariant.longitude,
                width: customVariant.width,
                height: customVariant.height,
                weight: customVariant.weight,
            }]
        }
    }

    const validateVariant = (variant) => {
        return variant.characteristics.filter(c => !c.valueName);
    }
    
    const addToCart = async (redirect = false) => {

        const emptyValues = validateVariant(customVariant);
        if(characteristicListData.length > 0 && emptyValues.length > 0){
            enqueueSnackbar(`Llena todos los campos para continuar.`, {
                variant: 'error'
            });
            return;
        }

        const processedProduct = buildVariantObject();
        // console.log("CART VARIANT", processedProduct);

        const cleanedProduct = cleanupProduct(processedProduct);
        console.log("CLEANED PRODUCT", cleanedProduct);

        // const newProduct = {...product, productVariants: [selectedVariant]};
        const newCart = addItemToCart(cleanedProduct);

        try {
            const userId = localStorage.getItem('UID');
            if(userId && !isLoading){
                setIsLoading(true);
                const result = await userCartAddItem(newCart);
                setIsLoading(false);
                if(result.result){
                    if(!newCart._id){
                        newCart._id = result.userCartId;
                    }
                    if(!redirect){
                        enqueueSnackbar(result.message, {
                            variant: 'success'
                        });

                        setOpenModal(true)
                    }
                }else{
                    enqueueSnackbar(result.message, {
                        variant: 'error'
                    });
                    return;
                }
            }else if(!redirect){
                enqueueSnackbar('articulo agregado al carrito.', {
                    variant: 'success'
                });
                setOpenModal(true)
            }
            globalUserDispatchContext({
                type: 'ADD',
                userCart: newCart
            });
            if(redirect){
                navigate('/cart');
            }
            
        } catch (error) {
            console.error(error);
            enqueueSnackbar('ocurrio un problema al agregar al carrito', {
                variant: 'error'
            });
        }
    };


    return (
        <Box minHeight={'100vh'} display={'flex'} flexDirection={'column'} sx={{backgroundColor:'#F8F8F8'}}>
            <Container 
                maxWidth="xl" 
                sx={{ 
                    display:'flex', 
                    flexDirection:'column', 
                    flexGrow: 1, 
                    width:'100%', 
                    px: {
                        xs:'1.25rem', 
                        md:'3.12rem', 
                        lg:'6.25rem'
                    }, 
                    pb: {xs:'3rem'}, 
                    pt: {
                        xs: '4.94rem',
                        md: '5.25rem', 
                        lg:'8.13rem'
                    } 
                }}
            >
                <Box>
                    <TitleText title={'Personalización'} customStyles={{display:{xs: 'flex', md:'none'}, pb:'0.62rem'}}/>
                </Box>
                <Box 
                    sx={{
                        width:'100%', 
                        display:'flex', 
                        flexDirection: {xs: 'column', md:'row'}, 
                        gap: {xs: '1.88rem', md:'1.5rem', lg:'1.91rem'},
                        maxWidth: {lg: '85rem'} 
                    }}
                >
                    <Box  sx={{
                        width: '100%',
                         ...styles.stackGrid, order:{xs: 2, md: 1} }}>
                        <Typography sx={styles.title}>
                            Personalización
                        </Typography>
                        {
                            product.characteristics && product.characteristics.filter(char => char.primary).map((item, index) => {
                                const primarySelected = customVariant.characteristics?.find(c => c.primary);

                                return (<InputSelect
                                    key={`${item.characteristicId}-${primarySelected?.valueName || 'empty'}`}
                                    textTitle={item.characteristicName} 
                                    options={item.values?.map(value => value.valueName)} 
                                    defaultValue={primarySelected?.valueName || ""} 
                                    handleOnchange={handleOnchange}
                                    primary={true}
                                />)
                            })
                        }
                        {!!characteristicListData.length && characteristicListData.filter(item => !item.primary).map((item, index) => {
                            const selected = customVariant.characteristics?.find(c => c.characteristicId === item.characteristicId);

                            return (
                                <InputSelect 
                                    key={`${item.characteristicId}-${selected?.valueName || 'empty'}`}
                                    textTitle={item.characteristicName} 
                                    options={item.values?.map(value => value.valueName)} 
                                    defaultValue={selected?.valueName || ""} 
                                    handleOnchange={handleOnchange}
                                    isDisabled={missingPrimarySelected}
                                    primary={false}
                                />
                            )
                        })}
                    </Box>
                    <Box  sx={{ width: '100%', order:{xs: 1, md: 2} }} >
                        {
                            !!product 
                            && <ProductResumeCard 
                                    product={product} 
                                    selectedVariant={customVariant} 
                                    dollarPrice={dollarPrice} 
                                    buttonText1={'Agregar al carrito'} 
                                    buttonText2={'Agregar y Finalizar Compra'} 
                                    handleButton1={() => {
                                        addToCart()
                                    }} 
                                    customStyle1={styles.buttonAddToCart} 
                                    handleButton2={() => addToCart(true)}
                                    customStyle2={styles.buttonAddToCartFinalize}
                                    isLoading={isLoading}
                                />
                        }
                    </Box>
                </Box>
                <CustomModal
                    openModal={openModal}
                    title={'Producto agregado al carrito'}
                    subtitle={'El producto se ha agregado al carrito correctamente.'}
                    customButton={
                        <ButtonStyled handleClick={() => setOpenModal(false)} text={'Aceptar'} />
                    }
                    onCloseModal={() => setOpenModal(false)}
                />
            </Container>
            <Footer/>
        </Box>
    )
}

export default CustomizeProductPage